"use client";

/**
 * Not Found Page (404)
 * 
 * แสดงเมื่อผู้ใช้เข้าถึงหน้าที่ไม่มีอยู่ในระบบ
 * พร้อมข้อความอธิบายและแนวทางแก้ไข
 * 
 * TOR Reference: Error Handling - ข้อความอธิบายชัดเจนเมื่อเกิดข้อผิดพลาด
 */

import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Home, Search, ArrowLeft, HelpCircle } from "lucide-react";

export default function NotFound() {
    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-b from-muted/30 to-background">
            {/* Header */}
            <header className="border-b bg-white/80 backdrop-blur">
                <div className="container mx-auto flex h-16 items-center px-4">
                    <Link href="/" className="flex items-center gap-3">
                        <Image
                            src="/logo_cus.png"
                            alt="กรมศุลกากร"
                            width={40}
                            height={40}
                            className="h-10 w-10 object-contain"
                        />
                        <div className="hidden sm:block">
                            <p className="text-sm font-semibold text-primary">กรมศุลกากร</p>
                            <p className="text-xs text-muted-foreground">Thai Customs Department</p>
                        </div>
                    </Link>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex items-center justify-center p-4">
                <div className="max-w-lg w-full text-center">
                    {/* Error Icon */}
                    <div className="mb-8">
                        <div className="inline-flex h-24 w-24 items-center justify-center rounded-full bg-orange-100 text-orange-600 mb-4">
                            <Search className="h-12 w-12" />
                        </div>
                        <h1 className="text-6xl font-bold text-primary mb-2">404</h1>
                    </div>

                    {/* Error Message */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-semibold text-foreground mb-3">
                            ไม่พบหน้าที่คุณต้องการ
                        </h2>
                        <p className="text-muted-foreground leading-relaxed">
                            หน้าที่คุณกำลังค้นหาอาจถูกย้าย ถูกลบ หรือ URL อาจพิมพ์ไม่ถูกต้อง
                            กรุณาตรวจสอบ URL อีกครั้ง หรือกลับไปหน้าหลัก
                        </p>
                    </div>

                    {/* Solutions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 text-left">
                        <div className="flex items-start gap-3">
                            <HelpCircle className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                            <div>
                                <p className="font-medium text-blue-900 mb-2">แนวทางแก้ไข:</p>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    <li>• ตรวจสอบว่าพิมพ์ URL ถูกต้อง</li>
                                    <li>• ลองค้นหาจากหน้าหลักอีกครั้ง</li>
                                    <li>• ติดต่อเจ้าหน้าที่หากยังพบปัญหา โทร 1164</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button asChild size="lg">
                            <Link href="/">
                                <Home className="mr-2 h-4 w-4" />
                                กลับหน้าหลัก
                            </Link>
                        </Button>
                        <Button variant="outline" size="lg" onClick={() => window.history.back()}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            ย้อนกลับ
                        </Button>
                    </div>

                    {/* Help Link */}
                    <p className="mt-8 text-sm text-muted-foreground">
                        หากต้องการความช่วยเหลือเพิ่มเติม{" "}
                        <a href="tel:1164" className="text-primary hover:underline font-medium">
                            โทร 1164
                        </a>{" "}
                        หรือ{" "}
                        <a href="mailto:complaint@customs.go.th" className="text-primary hover:underline font-medium">
                            complaint@customs.go.th
                        </a>
                    </p>
                </div>
            </main>
        </div>
    );
}
